
import React from 'react';

const SilicaMonitoringSection: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Silica Monitoring
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Silica, a common mineral in various construction materials like sand and concrete, presents significant health hazards when its fine particles are inhaled. These particles can potentially cause severe diseases such as silicosis and lung cancer.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Why Silica Monitoring is Critical
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Given these risks, industries such as construction and stone cutting require stringent monitoring of airborne silica to safeguard worker health and comply with occupational safety regulations. The adverse health outcomes associated with prolonged exposure to respirable crystalline silica highlight the necessity of monitoring.
              </p>
              <p className="text-gray-700 mb-8 leading-relaxed">
                OSHA mandates precise silica exposure limits, which necessitate rigorous monitoring. Such customized silica monitoring is a fundamental component of industry-specific health and safety protocols.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h4 className="font-semibold text-yellow-800 mb-4 flex items-center">
                  <i className="ri-alert-line mr-2"></i>
                  Health Risks of Silica Exposure
                </h4>
                <ul className="text-yellow-700 space-y-2">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line mt-1 mr-2 flex-shrink-0"></i>
                    <span>Silicosis (progressive lung disease)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line mt-1 mr-2 flex-shrink-0"></i>
                    <span>Lung cancer</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line mt-1 mr-2 flex-shrink-0"></i>
                    <span>Chronic obstructive pulmonary disease (COPD)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line mt-1 mr-2 flex-shrink-0"></i>
                    <span>Kidney disease</span>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Industries Requiring Silica Monitoring
              </h3>
              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-building-line text-blue-600 mr-3"></i>
                    Construction Industry
                  </h4>
                  <p className="text-gray-700">Concrete cutting, drilling, grinding, and demolition activities</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-hammer-line text-blue-600 mr-3"></i>
                    Stone Working
                  </h4>
                  <p className="text-gray-700">Cutting, shaping, and polishing natural and engineered stone</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-factory-line text-blue-600 mr-3"></i>
                    Manufacturing
                  </h4>
                  <p className="text-gray-700">Foundries, glass manufacturing, and ceramic production</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-pickaxe-line text-blue-600 mr-3"></i>
                    Mining & Quarrying
                  </h4>
                  <p className="text-gray-700">Extraction and processing of silica-containing materials</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-700 mb-8 text-lg">
              Safety management strategies will incorporate engineering controls and protective measures based on the monitoring outcomes to mitigate silica exposure. These comprehensive measures allow you to meet legal and regulatory requirements and significantly improve your employees' health and safety conditions.
            </p>
            <a 
              href="#contact-form" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Request Silica Assessment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SilicaMonitoringSection;
